import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, X, Mail, Phone, Building2, Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function AdminSubmissions() {
  const [items, setItems] = useState([]);
  const [creds, setCreds] = useState(null); // {email, password, partner_id}

  const load = async () => setItems((await api.get("/admin/submissions")).data);
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/submissions/${id}/approve`);
      toast.success("Contact approuvé — compte commerçant créé");
      if (data.temp_password) {
        setCreds({ email: data.merchant_email, password: data.temp_password });
      } else {
        toast.message("Le compte commerçant existait déjà — identifiants inchangés.");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur à la validation");
    }
  };
  const reject = async (id) => { await api.post(`/admin/submissions/${id}/reject`); toast.success("Contact rejeté"); load(); };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copié"); };

  const pending = items.filter((i) => i.status === "pending");
  const other = items.filter((i) => i.status !== "pending");

  return (
    <div className="space-y-8" data-testid="admin-submissions-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-navy-900/60 font-semibold">Soumissions</div>
        <h1 className="font-heading font-extrabold text-4xl text-navy-900 tracking-tighter mt-1">Demandes de contact</h1>
        <p className="text-muted-foreground mt-2 text-sm">À l'approbation, un compte commerçant est créé avec un mot de passe temporaire à transmettre au partenaire.</p>
      </div>

      <div className="space-y-4">
        <h2 className="font-heading font-bold text-lg text-navy-900">En attente ({pending.length})</h2>
        {pending.length === 0 && <p className="text-muted-foreground text-sm">Aucune demande en attente.</p>}
        {pending.map((s) => {
          const fullName = `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.contact_name || "—";
          return (
            <div key={s.id} className="card-premium p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-lime-600" />
                    <div className="font-heading font-bold text-xl text-navy-900">{s.business_name}</div>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">Contact : <span className="text-navy-900 font-medium">{fullName}</span></div>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm">
                    <div className="flex gap-1.5 text-navy-900"><Mail className="w-4 h-4 text-lime-600" />{s.email}</div>
                    <div className="flex gap-1.5 text-navy-900"><Phone className="w-4 h-4 text-lime-600" />{s.phone}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-3">Reçu le {(s.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  <button onClick={() => approve(s.id)} className="btn-lime text-sm" data-testid={`approve-${s.id}`}><Check className="w-4 h-4" /> Approuver</button>
                  <button onClick={() => reject(s.id)} className="btn-ghost text-sm border-destructive/30 text-destructive hover:bg-destructive/5" data-testid={`reject-${s.id}`}><X className="w-4 h-4" /> Refuser</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {other.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-lg text-navy-900">Historique</h2>
          <div className="card-premium overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted"><tr className="text-left">
                <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Commerce</th>
                <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Contact</th>
                <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Statut</th>
                <th className="p-4 text-xs uppercase tracking-wider text-navy-900/70">Reçu</th>
              </tr></thead>
              <tbody>
                {other.map((s) => (
                  <tr key={s.id} className="border-t border-navy-900/5">
                    <td className="p-4 font-semibold">{s.business_name}</td>
                    <td className="p-4">{s.email}</td>
                    <td className="p-4"><span className={`chip ${s.status === "approved" ? "bg-lime-500/20" : "bg-muted"} text-navy-900`}>{s.status}</span></td>
                    <td className="p-4 text-muted-foreground">{s.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!creds} onOpenChange={(v) => !v && setCreds(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-lime-600" /> Identifiants commerçant</DialogTitle>
            <DialogDescription>À transmettre au partenaire. Ces identifiants ne seront plus affichés.</DialogDescription>
          </DialogHeader>
          {creds && (
            <div className="space-y-4 mt-2">
              <CredRow label="Email" value={creds.email} onCopy={copy} />
              <CredRow label="Mot de passe temporaire" value={creds.password} onCopy={copy} />
              <div className="rounded-xl bg-lime-500/10 border border-lime-500/30 p-4 text-sm text-navy-900">
                Espace commerçant : <a href="/partenaire/login" className="font-semibold underline">/partenaire/login</a>
              </div>
              <button onClick={() => setCreds(null)} className="btn-navy w-full">Fermer</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredRow({ label, value, onCopy }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold text-navy-900/60 mb-1">{label}</div>
      <div className="flex items-center gap-2 rounded-xl bg-muted p-3">
        <code className="flex-1 text-sm text-navy-900 font-mono truncate">{value}</code>
        <button onClick={() => onCopy(value)} className="p-2 rounded-lg hover:bg-white"><Copy className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
